import { Injectable } from '@angular/core';
import {Page} from "./api.service";
import {HttpClient} from "@angular/common/http";
import {map, Observable} from "rxjs";
import {environment} from "../../environments/environment";
import {SimpleUser, User} from "./user.service";
import {AgendaItem} from "./room.service";
import { Activity, ActivityResponse } from './activity.service';

export class QuizTemplate {

  id?: string;
  subjectId?: string;
  status?: QuizTemplateStatus ;
  name: string;
  description: string ;
  createdBy?: SimpleUser ;
  createdAt?: Date ;
  questions: Question[] ;

  public constructor(name: string, description: string,  questions: Question[]) {
    this.name = name ;
    this.description = description ;
    this.questions = questions ;
  }

}

export class Quiz extends Activity {

  templateId: string ;
  questions: Question[] = [] ;
  currQuestionIndex?: number ;
  showingAnswer?: boolean ;

  constructor(templateId: string, forGroups:boolean) {
    super('QUIZ', forGroups);
    this.templateId = templateId ;
  }

}

export class QuizAgendaItem extends AgendaItem {

  templateName?: string ;
  templateId?: string ;
  quizId?: string ;

  constructor(title: string, forGroups: boolean) {
    super('QUIZ', forGroups, title) ;
  }
}

export class Question {

  markup: string
  points: number
  bonus: boolean
  choices: Choice[]
  explanation?:String ;
  issues?: QuestionIssue[]

  public constructor(markup: string, points: number, bonus: boolean, choices: Choice[]) {
    this.markup = markup;
    this.points = points ;
    this.bonus = bonus ;
    this.choices = choices ;
  }

}

export interface NonSpoilingQuestion {

  markup: string ;
  points: number ;
  bonus: boolean ;
  choices: string[] ;
}

export class Choice {

  markup: string
  correct?: boolean

  public constructor(markup: string, correct?: boolean) {
    this.markup = markup ;

    this.correct = !!correct ;
  }
}

export class Answer {
  choiceIndex?: number ;

  constructor(choiceIndex: number | undefined) {
    this.choiceIndex = choiceIndex ;
  }
}

export interface QuizResponse extends ActivityResponse {
  answers?: (Answer|null)[] ;
}

export type QuizTemplateStatus = 'VALID' | 'INVALID' | 'DELETED' ;
//export type QuizStatus = 'ACCEPTING_RESPONSES' | 'SHOWING_ANSWER' | 'COMPLETED' | 'CANCELED' ;
export type QuestionIssue = 'INSUFFICIENT_CHOICES' | 'NO_CORRECT_CHOICE' | 'MULTIPLE_CORRECT_CHOICES' | 'NO_POINTS' ;

export class QuizTemplateQuery {
  name?: string ;
  tags: string[] = [];
  statuses: QuizTemplateStatus[] = [];

  public constructor() {
  }
}




@Injectable({
  providedIn: 'root'
})
export class QuizService {

  private choiceLetters = ['A','B','C','D','E','F','G'] ;
  private choiceColors = [
    '#B449E2',
    '#F865B0',
    '#FDE74C',
    '#2EC4B6',
    '#EE8434',
    '#F3C178',
    '#048BA8'
  ]

  constructor(
    private http: HttpClient
  ) {

  }

  getCurrentQuiz(roomId: string): Observable<Quiz|undefined> {
    return this.http.get<Activity>(
      `${environment.apiHost}/rooms/${roomId}/activities/current`
    ).pipe(
      map( activity => {

         if (!activity || activity.type != 'QUIZ') 
            return undefined ;
          else 
            return activity as Quiz ;

      })
    );
  }

  startQuiz(roomId: string, quiz: Quiz): Observable<Quiz> {
    return this.http.post<Quiz>(
      `${environment.apiHost}/rooms/${roomId}/activities`,
      quiz
    ) ;
  }

  showAnswer(quiz: Quiz): Observable<Quiz> {

    return this.http.patch<Quiz>(
      `${environment.apiHost}/activities/${quiz.id}`,
      {
        type: 'QUIZ',
        showingAnswer: true
      }
    ) ;
  }

  showNextQuestion(quiz: Quiz): Observable<Quiz> {
    return this.http.patch<Quiz>(
      `${environment.apiHost}/activities/${quiz.id}`,
      {
        type: 'QUIZ',
        currQuestionIndex: quiz.currQuestionIndex! + 1,
        showingAnswer: false
      }
    );
  }

  cancel(quizId: string): Observable<Quiz> {

    return this.http.delete<Quiz>(
      `${environment.apiHost}/activities/${quizId}`
    );
  }

  getResponses(quizId: string, page: number ): Observable<Page<QuizResponse>> {

    let params = {
      page: (page) ? page : 0
    }

    return this.http.get<Page<QuizResponse>>(
      `${environment.apiHost}/activities/${quizId}/responses`,
      {params}
    ) ;
  }

  getMyResponse(quizId: string): Observable<QuizResponse> {

    return this.http.get<QuizResponse>(
      `${environment.apiHost}/activities/${quizId}/responses/mine`,
    ) as Observable<QuizResponse>;
  }

  updateMyResponse(quizId: string, questionIndex: number, choiceIndex: number): Observable<QuizResponse> {

    const answers = [];
    for (let i=0 ; i<questionIndex ; i++) {
      answers.push(null );
    }
    answers.push(new Answer(choiceIndex)) ;

    return this.http.patch(
      `${environment.apiHost}/activities/${quizId}/responses/mine`,
      {
        type: 'QUIZ',
        answers
      }
    ) as Observable<QuizResponse>;
  }

  makeAdjustment(quizId: string, userOrGroupId: string, questionIndex:number, answered: boolean, correct: boolean | null, explanation: string): Observable<QuizResponse> {

    return this.http.post<QuizResponse>(
      `${environment.apiHost}/activities/${quizId}/responses/${userOrGroupId}/adjustments`,
      {
        type: 'QUIZ',
        questionIndex,
        answered,
        correct,
        explanation
      }
    ) ;
  }



  public saveTemplate(subjectId: string, template:QuizTemplate): Observable<QuizTemplate> {

    if (template.id) {
      return this.http.put(
        `${environment.apiHost}/quiz-templates/${template.id}`,
        template
      ) as Observable<QuizTemplate>;
    } else {
      return this.http.post(
        `${environment.apiHost}/subjects/${subjectId}/quiz-templates`,
        template
      ) as Observable<QuizTemplate>;
    }
  }

  public getTemplate(templateId: string): Observable<QuizTemplate> {

    return this.http.get(
      `${environment.apiHost}/quiz-templates/${templateId}`
    ) as Observable<QuizTemplate>;

  }

  public getTemplates(subjectId: string, query: QuizTemplateQuery, page?: number): Observable<Page<QuizTemplate>>{

    let params: any = {
      q: JSON.stringify(query)
    }
    if (page) {
      params.page = page ;
    }

    return this.http.get(
      `${environment.apiHost}/subjects/${subjectId}/quiz-templates`,
      {
        params: params
      }
    ) as Observable<Page<QuizTemplate>>;

  }

  public deleteTemplate(templateId: string): Observable<QuizTemplate> {
    return this.http.delete(
      `${environment.apiHost}/quiz-templates/${templateId}`
    ) as Observable<QuizTemplate>;
  }

  getLetter(choiceIndex: number) {
    return this.choiceLetters[choiceIndex];
  }

  getColor(choiceIndex: number) {
    return this.choiceColors[choiceIndex];
  }



  /*
  getKnowledgeEffects(question: Question, attendee: Attendee): PointEffect[] {

    let knowledgePointsOffered = attendee.knowledgePointsOffered ;
    if (!question.bonus) {
      knowledgePointsOffered += question.points ;
    }

    const effects: PointEffect[] = []

    const correct = (attendee.knowledgePointsEarned + question.points) / knowledgePointsOffered ;
    effects.push({
      points: Math.round((correct - attendee.knowledgeScore) * 100 ),
      explanation: "if answered correctly"
    })

    const incorrect = attendee.knowledgePointsEarned / knowledgePointsOffered ;
    effects.push({
      points: Math.round((incorrect - attendee.knowledgeScore) * 100 ),
      explanation: "if answered incorrectly"
    })

    return effects ;
  }

  getParticipationEffects(question: Question, attendee: Attendee): PointEffect[] {

    let participationPointsOffered = attendee.participationPointsOffered ;
    if (!question.bonus) {
      participationPointsOffered += question.points ;
    }

    const effects: PointEffect[] = []

    const answered = (attendee.participationPointsEarned + question.points) / participationPointsOffered ;
    effects.push({
      points: Math.round((answered - attendee.participationScore) * 100 ),
      explanation: "if answered"
    })

    const unanswered = attendee.participationPointsEarned / participationPointsOffered ;
    effects.push({
      points: Math.round((unanswered - attendee.knowledgeScore) * 100 ),
      explanation: "if not answered"
    })

    return effects ;
  }*/



}
